import * as AuthActions from './auth.actions';
import { on, createReducer, Action } from '@ngrx/store';
import { ICustomerUser } from '@care-portals/api-interfaces';

export const AUTH_FEATURE_KEY = 'auth';

export interface State {
  isAuthenticated: boolean;
  errorMessage: string | null;
  user: ICustomerUser;
  token: string;
  showLoader: true;
  redirectUrl: string;
  forgotPasswordSuccess: boolean;
}

const initialState: State = {
  isAuthenticated: null,
  errorMessage: null,
  user: null,
  token: null,
  showLoader: true,
  redirectUrl: null,
  forgotPasswordSuccess: false
};

const _reducer = createReducer(
  { ...initialState },

  on(AuthActions.validateAuth, (state: any) => {
    return {
      ...state,
      showLoader: true,
      redirectUrl: getRedirectUrl()
    };
  }),

  // no valid token cookie found
  on(AuthActions.unauthenticatedState, (state: any) => {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      showLoader: false
    };
  }),

  on(AuthActions.logIn, (state: any, { username, password }) => {
    return {
      ...state,
      user: null,
      errorMessage: null,
      showLoader: true
    };
  }),

  on(AuthActions.logInSuccess, (state: any, { token }) => {
    return {
      ...state,
      isAuthenticated: true,
      token: token,
      errorMessage: null
    };
  }),

  on(AuthActions.logInFailure, (state: any, { error }) => {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      token: null,
      errorMessage: error?.message,
      showLoader: false
    };
  }),

  on(AuthActions.signUp, (state: any, { newCustomerDTO }) => {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      token: null,
      errorMessage: null,
      showLoader: true
    };
  }),

  on(AuthActions.signUpSuccess, (state: any, { newCustomerDTO }) => {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      token: null,
      errorMessage: null,
      showLoader: false
    };
  }),

  on(AuthActions.signUpFailure, (state: any, { error }) => {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      token: null,
      errorMessage: error?.message,
      showLoader: false
    };
  }),

  // fetch user from api
  on(AuthActions.loadUser, (state: any, { token }) => {
    return {
      ...state,
      isAuthenticated: true,
      token: token,
      user: null,
      errorMessage: null,
      showLoader: true
    };
  }),

  // user is fetched and validated
  on(AuthActions.loadUserSuccess, (state: any, { payload }) => {
    return {
      ...state,
      isAuthenticated: true,
      user: payload,
      errorMessage: null,
      showLoader: false
    };
  }),

  on(AuthActions.loadUserFailure, (state: any, { error }) => {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      token: null,
      errorMessage: error?.message,
      showLoader: false
    };
  }),

  on(AuthActions.logOut, (state: any) => {
    return {
      ...initialState
    };
  }),

  on(AuthActions.logOutSuccess, (state: any) => {
    return {
      ...state,
      showLoader: false
    };
  }),

  on(AuthActions.forgotPassword, (state: any) => {
    return {
      ...state,
      errorMessage: null,
      showLoader: true,
      forgotPasswordSuccess: false
    };
  }),

  on(AuthActions.forgotPasswordSuccess, (state: any) => {
    return {
      ...state,
      showLoader: false,
      forgotPasswordSuccess: true
    };
  }),

  on(AuthActions.forgotPasswordFailure, (state: any, { error }) => {
    return {
      ...state,
      errorMessage: error?.message,
      showLoader: false,
      forgotPasswordSuccess: false
    };
  }),

  on(AuthActions.resetPassword, (state: any) => {
    return {
      ...state,
      errorMessage: null,
      showLoader: true
    };
  }),

  on(AuthActions.resetPasswordSuccess, (state: any) => {
    return {
      ...state,
      showLoader: false
    };
  }),

  on(AuthActions.resetPasswordFailure, (state: any, { error }) => {
    return {
      ...state,
      errorMessage: error?.message,
      showLoader: false
    };
  }),

  on(AuthActions.loginWithVerifyLink, (state: any) => {
    return {
      ...state,
      errorMessage: null,
      showLoader: true
    };
  }),

  on(AuthActions.loginWithVerifyLinkSuccess, (state: any) => {
    return {
      ...state,
      showLoader: false
    };
  }),

  on(AuthActions.loginWithVerifyLinkFailure, (state: any, { error }) => {
    return {
      ...state,
      errorMessage: error?.message,
      showLoader: false
    };
  })
);

export function reducer(state: State, action: Action) {
  return _reducer(state, action);
}

function getRedirectUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  const redirectUrl = urlParams.get('redirect');
  if (redirectUrl) {
    try {
      return decodeURIComponent(redirectUrl);
    } catch (e) {
      return redirectUrl;
    }
  } else {
    // paths in the /auth context are ignored (i.e. auth/login auth/signup auth/forgot auth/reset)
    const url = window.location.pathname + window.location.search;
    return url.includes('auth') ? null : url;
  }
}
